import { normalizeAmount } from "@frontend/utils/amount";

import type {
  AccountantValues,
  ClientContactValues,
  InterventionValues,
  PlannedExpenseValues,
  ProjectValues,
} from "../schemas/project";
import type {
  InterventionInput,
  PlannedExpenseInput,
  Project,
  ProjectAccountantInput,
  ProjectClientContactInput,
  ProjectInput,
} from "../types";

function blankToNull(value: string | null): string | null {
  return null === value || "" === value.trim() ? null : value;
}

/**
 * Only the block the project is travels: the API fills one of the two and
 * refuses the other, and the form keeps both so its fields never move.
 */
export function toProjectInput(values: ProjectValues): ProjectInput {
  const intervention = "intervention" === values.type;

  return {
    lockVersion: values.lockVersion,
    name: blankToNull(values.name),
    statusId: values.statusId,
    serviceGroupId: values.serviceGroupId,
    clientId: values.clientId,
    locationId: values.locationId,
    deliveryLocationId: values.deliveryLocationId,
    billingAddressId: values.billingAddressId,
    billingContactId: values.billingContactId,
    billingContactInBill: values.billingContactInBill,
    billingAttorneyId: values.billingAttorneyId,
    billingClientReference: blankToNull(values.billingClientReference),
    billingClientReferenceInBill: values.billingClientReferenceInBill,
    billingWording: blankToNull(values.billingWording),
    billingComments: blankToNull(values.billingComments),
    comments: blankToNull(values.comments),
    quotationNumber: blankToNull(values.quotationNumber),
    billingTypeJustification: blankToNull(values.billingTypeJustification),
    clientContacts: values.clientContacts.map(toClientContactInput),
    accountants: values.accountants.map(toAccountantInput),
    commercialCondition: {
      paymentTypeId: values.commercialCondition.paymentTypeId,
      interventionPaymentPeriodId:
        values.commercialCondition.interventionPaymentPeriodId,
      paymentEmail: blankToNull(values.commercialCondition.paymentEmail),
      paymentEmailCopyTo: blankToNull(
        values.commercialCondition.paymentEmailCopyTo,
      ),
    },
    subscription: intervention
      ? null
      : {
          initialAffiliateId: values.subscription.initialAffiliateId,
          billingProjectId: values.subscription.billingProjectId,
        },
    intervention: intervention
      ? toInterventionInput(values.intervention)
      : null,
  };
}

function toClientContactInput(
  contact: ClientContactValues,
): ProjectClientContactInput {
  return {
    id: contact.id,
    contactId: contact.contactId,
    typeId: contact.typeId,
  };
}

function toAccountantInput(
  accountant: AccountantValues,
): ProjectAccountantInput {
  return {
    id: accountant.id,
    employeeId: accountant.employeeId,
    typeId: accountant.typeId,
  };
}

function toPlannedExpenseInput(
  expense: PlannedExpenseValues,
): PlannedExpenseInput {
  return {
    id: expense.id,
    typeId: expense.typeId,
    quantity: expense.quantity,
    unitPrice: normalizeAmount(expense.unitPrice),
    recurrenceId: expense.recurrenceId,
  };
}

function toInterventionInput(
  intervention: InterventionValues,
): InterventionInput {
  return {
    taskTypeId: intervention.taskTypeId,
    subTaskTypeId: intervention.subTaskTypeId,
    dateBegin: intervention.dateBegin,
    dateEnd: intervention.dateEnd,
    interventionDays: intervention.interventionDays,
    refundLunch: intervention.refundLunch,
    refundRide: intervention.refundRide,
    rideDuration: intervention.rideDuration,
    units: intervention.units,
    unitTypeId: intervention.unitTypeId,
    voucherNumber: blankToNull(intervention.voucherNumber),
    recurrence: blankToNull(intervention.recurrence),
    exploitationComments: blankToNull(intervention.exploitationComments),
    estimatedHours: blankToNull(intervention.estimatedHours),
    estimatedDays: intervention.estimatedDays,
    estimatedWorkers: intervention.estimatedWorkers,
    estimatedValuedHours: blankToNull(intervention.estimatedValuedHours),
    startingSiteId: intervention.startingSiteId,
    subscriptionId: intervention.subscriptionId,
    plannedExpenses: intervention.plannedExpenses.map(toPlannedExpenseInput),
    dailySchedule: {
      arrival: blankToNull(intervention.dailySchedule.arrival),
      departure: blankToNull(intervention.dailySchedule.departure),
      hours: intervention.dailySchedule.hours.map((range) => ({
        id: range.id,
        begin: blankToNull(range.begin),
        end: blankToNull(range.end),
      })),
    },
  };
}

export function toProjectValues(project: Project): ProjectValues {
  return {
    type: project.type,
    lockVersion: project.lockVersion,
    name: project.name,
    statusId: project.statusId,
    serviceGroupId: project.serviceGroupId,
    clientId: project.clientId,
    locationId: project.locationId,
    deliveryLocationId: project.deliveryLocationId,
    billingAddressId: project.billingAddressId,
    billingContactId: project.billingContactId,
    billingContactInBill: project.billingContactInBill,
    billingAttorneyId: project.billingAttorneyId,
    billingClientReference: project.billingClientReference,
    billingClientReferenceInBill: project.billingClientReferenceInBill,
    billingWording: project.billingWording,
    billingComments: project.billingComments,
    comments: project.comments,
    quotationNumber: project.quotationNumber,
    billingTypeJustification: project.billingTypeJustification,
    clientContacts: project.clientContacts.map((contact) => ({
      id: contact.id,
      contactId: contact.contactId,
      typeId: contact.typeId,
    })),
    accountants: project.accountants.map((accountant) => ({
      id: accountant.id,
      employeeId: accountant.employeeId,
      typeId: accountant.typeId,
    })),
    commercialCondition: {
      paymentTypeId: project.commercialCondition?.paymentTypeId ?? null,
      interventionPaymentPeriodId:
        project.commercialCondition?.interventionPaymentPeriodId ?? null,
      paymentEmail: project.commercialCondition?.paymentEmail ?? null,
      paymentEmailCopyTo:
        project.commercialCondition?.paymentEmailCopyTo ?? null,
    },
    subscription: {
      initialAffiliateId: project.subscription?.initialAffiliateId ?? null,
      billingProjectId: project.subscription?.billingProjectId ?? null,
    },
    intervention: {
      taskTypeId: project.intervention?.taskTypeId ?? null,
      subTaskTypeId: project.intervention?.subTaskTypeId ?? null,
      dateBegin: project.intervention?.dateBegin ?? null,
      dateEnd: project.intervention?.dateEnd ?? null,
      interventionDays: project.intervention?.interventionDays ?? null,
      refundLunch: project.intervention?.refundLunch ?? false,
      refundRide: project.intervention?.refundRide ?? false,
      rideDuration: project.intervention?.rideDuration ?? null,
      units: project.intervention?.units ?? null,
      unitTypeId: project.intervention?.unitTypeId ?? null,
      voucherNumber: project.intervention?.voucherNumber ?? null,
      recurrence: project.intervention?.recurrence ?? null,
      exploitationComments: project.intervention?.exploitationComments ?? null,
      estimatedHours: project.intervention?.estimatedHours ?? null,
      estimatedDays: project.intervention?.estimatedDays ?? null,
      estimatedWorkers: project.intervention?.estimatedWorkers ?? null,
      estimatedValuedHours: project.intervention?.estimatedValuedHours ?? null,
      startingSiteId: project.intervention?.startingSiteId ?? null,
      subscriptionId: project.intervention?.subscriptionId ?? null,
      plannedExpenses: (project.intervention?.plannedExpenses ?? []).map(
        (expense) => ({
          id: expense.id,
          typeId: expense.typeId,
          quantity: expense.quantity,
          unitPrice: expense.unitPrice,
          recurrenceId: expense.recurrenceId,
        }),
      ),
      dailySchedule: {
        arrival: project.intervention?.dailySchedule?.arrival ?? null,
        departure: project.intervention?.dailySchedule?.departure ?? null,
        hours: (project.intervention?.dailySchedule?.hours ?? []).map(
          (range) => ({ id: range.id, begin: range.begin, end: range.end }),
        ),
      },
    },
  };
}
